import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Map, Building2, Globe2, TrendingUp, MessageSquareText, Lock, CheckCircle2, XCircle } from "lucide-react";
import AppNav from "../components/AppNav";
import { useDarkMode } from "../hooks/useDarkMode";
import { BACKEND_URL } from "../lib/backend";
import { setCanonical, setDescription, setHreflang, setJsonLd, SITE_URL } from "../lib/seo";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum, applyMult } from "./theme";
import { DeltaBadge, SourceChip, SkeletonCard, BreakBadge } from "./components";
import { regionName } from "./regionNames";
import { LangToggle } from "./PulseLayout";
import { ForecastKpiCard } from "./forecast";
import { savePulseNext } from "./PulseGate";
import InlineRegister from "./InlineRegister";
import "./pulse.css";

const SOURCES = ["ISTAT", "Banca d'Italia", "Eurostat", "OECD"];
const PANELS = [
  { key: "regions", Icon: Map, bars: [70, 45, 90, 60, 35, 80] },
  { key: "provinces", Icon: Building2, bars: [40, 85, 55, 30, 75, 50] },
  { key: "markets", Icon: Globe2, bars: [90, 70, 55, 45, 35, 25] },
  { key: "outlook", Icon: TrendingUp, bars: [30, 45, 40, 60, 75, 90] },
  { key: "ask", Icon: MessageSquareText, bars: [60, 60, 60, 40, 40, 20] },
];

const kpiValue = (k, lang) => `${fmtCompact(applyMult(k.value, k.unit_mult), lang)}${k.unit === "EUR" ? " €" : ""}`;
const kpiLabel = (k, lang) => {
  if (k.region_code) return `${lang === "it" ? `Regione n.${k.id.slice(-1)} per presenze` : `#${k.id.slice(-1)} region by nights`}: ${regionName(k.region_code, lang, k.region_label)}`;
  return lang === "it" ? k.label_it : k.label_en;
};

/** Blurred, data-free preview of a gated panel (no interactive data for anonymous visitors). */
const PanelPreview = ({ p, dm, t }) => {
  const T = tokens(dm);
  return (
    <div className="relative overflow-hidden" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid={`landing-panel-${p.key}`}>
      <div className="h-28 flex items-end gap-2 px-6 pt-5" style={{ filter: "blur(4px)", opacity: 0.55 }} aria-hidden="true">
        {p.bars.map((h, i) => <div key={i} className="flex-1" style={{ height: `${h}%`, background: i % 2 ? T.brand : T.accent, opacity: 0.8 }} />)}
      </div>
      <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1" style={{ background: T.bg, color: T.muted, borderRadius: 2 }}>
        <Lock className="w-3 h-3" aria-hidden="true" /> {t("landing_locked")}
      </div>
      <div className="p-5 flex items-start gap-3">
        <p.Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: T.accent }} aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">{t(`landing_panel_${p.key}`)}</p>
          <p className="text-xs mt-1 leading-relaxed" style={{ color: T.muted }}>{t(`landing_panel_${p.key}_desc`)}</p>
        </div>
      </div>
    </div>
  );
};

const CtaButton = ({ onClick, dm, t, testid }) => {
  const T = tokens(dm);
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold" style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
      {t("landing_cta")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
    </button>
  );
};

/** Public Italy landing (anonymous /pulse/italy): real warehouse numbers, forecasts with
 *  their confidence rule, the latest forecast-vs-observed check, blurred previews of the
 *  gated panels and an in-page magic-link signup that lands on the Italy Deep Dive. */
export default function ItalyLanding() {
  const [dm] = useDarkMode();
  const { t, lang, withLang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);
  const next = "/pulse/italy";
  const target = lang === "it" ? `${next}?lang=it` : next;

  useEffect(() => {
    let on = true;
    axios.get(`${BACKEND_URL}/api/pulse/teaser`, { params: { scope: "italy" } })
      .then((r) => { if (on) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (on) setLoading(false); });
    try {
      if (!sessionStorage.getItem("pulse_italy_landing_viewed")) {
        sessionStorage.setItem("pulse_italy_landing_viewed", "1");
        axios.post(`${BACKEND_URL}/api/pulse/landing-view`, { page: "italy" }).catch(() => {});
      }
    } catch { /* private mode */ }
    return () => { on = false; };
  }, []);

  // SEO: title/meta per language, canonical, hreflang, JSON-LD Dataset + WebPage
  useEffect(() => {
    const prev = document.title;
    document.title = lang === "it" ? "Pulse Italia — il turismo in Italia in numeri ufficiali | TourismIntel"
      : "Pulse Italy — Italy's tourism in official numbers | TourismIntel";
    setDescription(t("landing_meta_desc"));
    setCanonical(`${SITE_URL}/pulse/italy`);
    const offs = [setHreflang("en", `${SITE_URL}/pulse/italy`), setHreflang("it", `${SITE_URL}/pulse/italy?lang=it`),
      setHreflang("x-default", `${SITE_URL}/pulse/italy`)];
    const og = (p, v) => { let m = document.querySelector(`meta[property="${p}"]`); if (!m) { m = document.createElement("meta"); m.setAttribute("property", p); document.head.appendChild(m); } m.setAttribute("content", v); };
    og("og:title", document.title); og("og:description", t("landing_meta_desc")); og("og:url", `${SITE_URL}/pulse/italy`); og("og:locale", lang === "it" ? "it_IT" : "en_US");
    return () => { document.title = prev; offs.forEach((off) => off && off()); };
  }, [lang, t]);

  useEffect(() => {
    if (!data) return;
    setJsonLd("pulse-italy-webpage", {
      "@context": "https://schema.org", "@type": "WebPage", name: document.title, url: `${SITE_URL}/pulse/italy`,
      inLanguage: lang, description: t("landing_meta_desc"), isPartOf: { "@type": "WebSite", name: "TourismIntel", url: SITE_URL },
      author: { "@type": "Person", name: "Mirko Lalli", url: "https://mirkolalli.me" },
    });
    setJsonLd("pulse-italy-dataset", {
      "@context": "https://schema.org", "@type": "Dataset", name: lang === "it" ? "Pulse Italia — statistiche ufficiali del turismo in Italia" : "Pulse Italy — official tourism statistics for Italy",
      description: t("landing_meta_desc"), url: `${SITE_URL}/pulse/italy`, inLanguage: lang, isAccessibleForFree: true,
      creator: { "@type": "Organization", name: "TourismIntel", url: SITE_URL },
      sourceOrganization: SOURCES.map((n) => ({ "@type": "Organization", name: n })),
      temporalCoverage: `${(data.kpis || []).map((k) => String(k.period || "")).sort()[0] || ""}/..`,
      spatialCoverage: { "@type": "Place", name: "Italy" },
      distribution: [
        { "@type": "DataDownload", encodingFormat: "application/json", contentUrl: `${SITE_URL}/api/pulse/teaser?scope=italy` },
        { "@type": "DataDownload", encodingFormat: "text/markdown", contentUrl: `${SITE_URL}/api/pulse/teaser.md?scope=italy${lang === "it" ? "&lang=it" : ""}` },
      ],
      variableMeasured: (data.kpis || []).map((k) => ({ "@type": "PropertyValue", name: kpiLabel(k, lang), value: k.value, unitText: k.unit || "count" })),
    });
  }, [data, lang, t]);

  const scrollToForm = (resume) => {
    try { localStorage.removeItem("pulse_next"); } catch { /* noop */ }
    savePulseNext(resume || target);     // the form keeps a deeper /pulse `next` (e.g. the accuracy table)
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    formRef.current?.querySelector("input[type=email]")?.focus({ preventScroll: true });
  };
  const kpis = data?.kpis || [];
  const fks = (data?.forecast_kpis || []).filter((k) => k.confidence === "alta");
  const acc = data?.accuracy_last;
  const n = kpis.length + fks.length;
  const gridCols = n % 4 === 0 ? "lg:grid-cols-4" : "lg:grid-cols-3";

  return (
    <div className="pulse-root min-h-screen pb-24 md:pb-0" style={{ background: T.bg, color: T.text }} data-testid="italy-landing">
      <AppNav darkMode={dm} />
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6">
        {/* Hero */}
        <section className="pt-16 pb-10 md:pt-24 md:pb-14">
          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: T.accent }}>{t("landing_eyebrow")}</p>
            <LangToggle dm={dm} />
          </div>
          <h1 className="pulse-serif text-4xl sm:text-5xl lg:text-6xl font-medium max-w-3xl leading-tight" data-testid="landing-title">{t("landing_title")}</h1>
          <p className="text-base md:text-lg mt-5 max-w-2xl leading-relaxed" style={{ color: T.muted }}>{t("landing_sub")}</p>
          <div className="flex items-center gap-2 mt-6 flex-wrap" data-testid="landing-sources">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>{t("landing_sources")}</span>
            {SOURCES.map((s) => <span key={s} className="text-xs font-semibold px-2.5 py-1" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 2 }}>{s}</span>)}
          </div>
          <div className="flex items-center gap-4 mt-8 flex-wrap">
            <CtaButton onClick={() => scrollToForm()} dm={dm} t={t} testid="landing-cta-hero" />
            <Link to="/auth" onClick={() => savePulseNext(target)} className="text-sm font-semibold underline underline-offset-4" style={{ color: T.muted }} data-testid="landing-login-hero">{t("lr_have_account")} {t("lr_login")}</Link>
          </div>
          <p className="text-xs mt-3" style={{ color: T.muted }}>{t("landing_cta_sub")}</p>
        </section>

        {/* KPIs */}
        <section className="pb-12" aria-labelledby="landing-kpis-title">
          <h2 id="landing-kpis-title" className="pulse-serif text-lg font-semibold mb-4">{t("landing_kpis_title")}</h2>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols}`} style={{ gap: 1, background: T.border, border: `1px solid ${T.border}` }} data-testid="landing-kpis">
            {loading && [0, 1, 2, 3].map((i) => <SkeletonCard key={i} h={150} dm={dm} />)}
            {!loading && kpis.map((k) => (
              <div key={k.id} className="p-5 flex flex-col gap-2" style={{ background: T.surface }} data-testid={`landing-kpi-${k.id}`}>
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>{kpiLabel(k, lang)}</span>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="pulse-num text-3xl md:text-4xl font-semibold">{kpiValue(k, lang)}</span>
                  <DeltaBadge pct={k.yoy_pct} dm={dm} />
                  {k.break_affected && <BreakBadge brk={k.series_break} dm={dm} short testid={`landing-kpi-break-${k.id}`} />}
                </div>
                <SourceChip source={k.source_org} dataset={k.dataset_code} period={(lang === "it" ? k.period_it : k.period_en) || k.period} dm={dm} />
              </div>
            ))}
            {!loading && fks.map((fk) => <ForecastKpiCard key={fk.id} fk={fk} dm={dm} testidPrefix="landing-kpi-forecast" />)}
          </div>
          {!loading && (
            <p className="text-xs mt-3" style={{ color: T.muted }}>
              {t("landing_kpis_note")}{" "}
              <a href={`${BACKEND_URL}/api/pulse/teaser.md?scope=italy${lang === "it" ? "&lang=it" : ""}`} className="underline" style={{ color: T.accent }} data-testid="landing-md-link">{t("landing_md_link")}</a>
            </p>
          )}
        </section>

        {/* Register (in page) */}
        <section ref={formRef} className="pb-14 scroll-mt-24" aria-labelledby="landing-register-title" data-testid="landing-register-section">
          <div className="p-6 md:p-8" style={{ background: T.surface, border: `1px solid ${T.border}`, borderLeftWidth: 3, borderLeftColor: T.brand }}>
            <h2 id="landing-register-title" className="pulse-serif text-2xl font-medium">{t("landing_cta")}</h2>
            <p className="text-sm mt-2 mb-5 max-w-2xl" style={{ color: T.muted }}>{t("landing_register_sub")}</p>
            <InlineRegister dm={dm} next={next} />
          </div>
        </section>

        {/* Forecast vs observed */}
        <section className="pb-14" aria-labelledby="landing-acc-title">
          <h2 id="landing-acc-title" className="pulse-serif text-lg font-semibold mb-4">{t("landing_acc_title")}</h2>
          {loading && <SkeletonCard h={90} dm={dm} />}
          {!loading && !acc && <p className="text-sm" style={{ color: T.muted }} data-testid="landing-acc-empty">{t("landing_acc_empty")}</p>}
          {!loading && acc && (
            <div className="p-5 flex flex-col md:flex-row md:items-center gap-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="landing-accuracy">
              {acc.inside_80 ? <CheckCircle2 className="w-6 h-6 flex-shrink-0" style={{ color: T.accent }} aria-hidden="true" /> : <XCircle className="w-6 h-6 flex-shrink-0" style={{ color: "#B42318" }} aria-hidden="true" />}
              <p className="text-sm leading-relaxed flex-1">
                <span className="font-semibold">{lang === "it" ? acc.label_it : acc.label_en} · {lang === "it" ? acc.period_label_it : acc.period_label_en}</span>
                {" — "}{t("landing_acc_row", { q50: fmtCompact(acc.q50, lang), obs: fmtCompact(acc.observed, lang), err: `${acc.pct_err > 0 ? "+" : ""}${fmtNum(acc.pct_err, lang, 1)}%` })}{" "}
                <span className="font-semibold" style={{ color: acc.inside_80 ? T.accent : "#B42318" }}>{acc.inside_80 ? t("landing_acc_inside") : t("landing_acc_outside")}</span>
                <span className="text-xs block mt-1" style={{ color: T.muted }}>{t("landing_acc_run", { run: acc.run_id })}</span>
              </p>
              <Link to="/pulse/forecast-accuracy" onClick={(e) => { e.preventDefault(); scrollToForm(withLang("/pulse/forecast-accuracy")); }}
                className="inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-4 whitespace-nowrap" style={{ color: T.accent }} data-testid="landing-accuracy-link">
                <Lock className="w-3.5 h-3.5" aria-hidden="true" /> {t("landing_acc_link")}
              </Link>
            </div>
          )}
        </section>

        {/* Inside */}
        <section className="pb-14" aria-labelledby="landing-inside-title">
          <h2 id="landing-inside-title" className="pulse-serif text-lg font-semibold mb-4">{t("landing_inside_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PANELS.map((p) => <PanelPreview key={p.key} p={p} dm={dm} t={t} />)}
          </div>
        </section>

        {/* Curator + final CTA */}
        <section className="pb-16 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="landing-footer-cta">
          <p className="text-sm" style={{ color: T.muted }}>
            <span className="font-semibold" style={{ color: T.text }}>{t("landing_curator")}</span> · Mirko Lalli
            <span className="block text-xs mt-1">{t("landing_curator_sub")}</span>
          </p>
          <CtaButton onClick={() => scrollToForm()} dm={dm} t={t} testid="landing-cta-footer" />
        </section>
      </main>

      {/* Mobile sticky CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 px-4 py-3 flex items-center justify-between gap-3" style={{ background: T.surface, borderTop: `1px solid ${T.border}` }} data-testid="landing-sticky-cta">
        <span className="text-xs font-semibold leading-tight">{t("landing_sticky")}</span>
        <button type="button" onClick={() => scrollToForm()} className="px-4 py-2 text-xs font-bold whitespace-nowrap" style={{ background: T.brand, color: "#fff", borderRadius: 2 }} data-testid="landing-cta-sticky">{t("landing_sticky_btn")}</button>
      </div>
    </div>
  );
}
